<!--Copy right - © 2024 tecera gmbh-->
<script>
    import { onMount } from "svelte";
    import Highcharts from "highcharts/highstock";
    import HighchartsMore from "highcharts/highcharts-more";
    import Accessibility from "highcharts/modules/accessibility";
    import Boost from "highcharts/modules/boost";

    import {
        selected_device,
        current_device_data,
        chart,
        extended_device_channel_list,
        hymo_data_end_date,
    } from "../stores.js";

    let is_mounted = false;
    let chartContainer;
    let channels = [];
    let chart_data = [];
    let y_axis = [];
    let colors = [
        "#7CB5EC", // Light blue
        "#f7A35C", // Light orange
        "#31AC0D", // Green
        "#A960AF", // Purple
        "#FF0066", // Bright pink
        "#666666", // Gray
        "#D1C924", // Yellow
        "#EEAAEE", // Light pink
    ];
    var icon_dictionary = {
        Hydrantendeckel: {
            min: '<i class="axisIcon fa fa-moon" style="width: 12px; height: 12px;"></i>',
            max: '<i class="axisIcon fa fa-sun" style="width: 12px; height: 12px;"></i>',
        },
        Magnetring: {
            min: '<i class="axisIcon fa fa-unlink" style="width: 12px; height: 12px;"></i>',
            max: '<i class="axisIcon fa fa-magnet" style="width: 12px; height: 12px;"></i>',
        },
        Wasserdruck: {
            min: '<i class="axisIcon fa fa-droplet-slash" style="width: 12px; height: 12px;"></i>',
            max: '<i class="axisIcon fa fa-droplet" style="width: 12px; height: 12px;"></i>',
        },
        Batteriestatus: {
            min: '<i class="axisIcon fa fa-battery-full" style="width: 12px; height: 12px;"></i>',
            max: '<i class="axisIcon fa fa-battery-quarter" style="width: 12px; height: 12px;"></i>',
        },
        Default: {
            min: '<i class="axisIcon fa fa-check-square" style="width: 12px; height: 12px;"></i>',
            max: '<i class="axisIcon fa fa-times-rectangle" style="width: 12px; height: 12px;"></i>',
        },
    };

    onMount(() => {
        is_mounted = true;
    });

    var y_axis_formatter = function () {
        var title = this.axis.options.title.text.trim();
        var icons = icon_dictionary[title];
        if (icons) {
            return this.value == 1 ? icons.max : icons.min;
        } else {
            return this.value == 1
                ? '<i class="axisIcon fa fa-check-square" style="width: 12px; height: 12px;"></i>'
                : '<i class="axisIcon fa fa-times-rectangle" style="width: 12px; height: 12px;"></i>';
        }
    };

    function update_graph() {
        if ($selected_device) {
            let alarms = [];
            $selected_device.channels.forEach((channel) => {
                if (channel.measurand === "Pegelstand" && channel.alarms) {
                    alarms.push(...channel.alarms);
                }
            });
            let alarm_values = [];
            alarms.forEach((alarm) => {
                if (
                    alarm.max_alarm_active === 1 &&
                    alarm.max_threshold !== null
                ) {
                    alarm_values.push(alarm.max_threshold);
                }
                if (
                    alarm.min_alarm_active === 1 &&
                    alarm.min_threshold !== null
                ) {
                    alarm_values.push(alarm.min_threshold);
                }
            });

            channels = [];
            channels = $extended_device_channel_list;
            // Transform fetched data into chart data
            chart_data = channels.map((channel, index) => {
                let channel_config = {
                    lineColor: colors[index],
                    name: channel.measurand,
                    type: "line",
                    data: [],
                    yAxis: index,
                    channel_id: channel.channel_id,
                    visible: channel.visible,
                };
                if (channel.boolean_0) {
                    channel_config["step"] = "left";
                    channel_config["lineWidth"] = 2;
                }
                return channel_config;
            });

            y_axis = channels.map((channel, index) => {
                let y_axis_config = {
                    showLastLabel: true,
                    lineColor: colors[index],
                    title: {
                        style: {
                            color: colors[index],
                            fontWeight: "bold",
                        },
                        text: channel.measurand + " (" + channel.unit + ")",
                        // margin: -8,
                    },
                    labels: {
                        useHTML: true,
                    },
                    opposite: index % 2 === 1,
                    lineWidth: 2,
                    visible: channel.visible,
                };
                if (channel.measurand === "Pegelstand") {
                    let plotlines = [];
                    let highest_alarm_value = Math.max(0,...alarm_values);
                    alarm_values.forEach((alarm_value) => {
                        plotlines.push({
                            color: "red",
                            value: alarm_value,
                            width: 1,
                            dashStyle: "Dash",
                            zIndex: 5,
                            label: {
                                text: alarm_value +" mm",
                                style: {
                                    color: "red",
                                },
                            },
                        });
                    });

                    y_axis_config["plotLines"] = plotlines;
                    // y_axis_config["minRange"] = 250;
                    // y_axis_config["minPadding"] = 0.1;
                    // y_axis_config["maxPadding"] = 0.1;
                    y_axis_config["min"] = 0;
                    y_axis_config["softMax"] = highest_alarm_value + 200;
                }
                if (channel.boolean_0) {
                    y_axis_config["tickInterval"] = 1;
                    y_axis_config["min"] = 0;
                    y_axis_config["max"] = 1;
                    y_axis_config["title"]["text"] = channel.measurand;
                    y_axis_config["labels"]["formatter"] = y_axis_formatter;
                    y_axis_config["labels"]["roation"] = -90;
                    y_axis_config["startOnTick"] = false;
                    y_axis_config["endOnTick"] = false;
                }
                return y_axis_config;
            });
            chartOptions.series = chart_data;
            chartOptions.yAxis = y_axis;
            $chart = Highcharts.StockChart(chartContainer, chartOptions);
        }
    }
    function update_graph_data() {
        chart_data = channels.map((channel, index) => {
            let last_value = null;
            let series_data = $current_device_data.map((data) => {
                last_value =
                    data[channel.name] === null
                        ? last_value
                        : data[channel.name];
                return [new Date(data.send_at).getTime(), data[channel.name]];
            });

            if (channel.boolean_0) {
                series_data.push([
                    new Date($hymo_data_end_date).getTime(),
                    last_value,
                ]);
            }
            return {
                lineColor: colors[index],
                name: channel.measurand,
                type: "line",
                data: series_data,
                yAxis: index,
                visible: channel.visible,
                channel_id: channel.channel_id,
                step: "left",
            };
        });

        // Assuming `$chart` is your Highcharts chart instance
        chart_data.forEach((seriesData, index) => {
            if ($chart.series[index]) {
                $chart.series[index].setData(seriesData.data);
            }
        });
    }

    $: {
        if ($selected_device && is_mounted) {
            update_graph();
        }
    }
    $: {
        if (
            $current_device_data &&
            $current_device_data.length > 0 &&
            is_mounted
        ) {
            update_graph_data();
        }
    }

    var formatter = function () {
        var xyArr = [];
        xyArr.push(
            Highcharts.dateFormat(
                "%d.%m.%Y %H:%M:%S",
                new Date(this.x),
            ).toLocaleString(),
        );
        xyArr.push(
            "<table><tr>" +
                "<th>Name</th>" +
                '<th><span style="margin-left: 1em">Wert</span></th>' +
                '<th><span style="margin-left: 1em">Min</span></th>' +
                '<th><span style="margin-left: 1em">Max</span></th>' +
                "</tr>",
        );

        this.points.forEach(function (point) {
            var v = Highcharts.numberFormat(
                point.y,
                Math.abs(point.y) < 10000.0 ? 2 : 1,
                ",",
            );
            var min = Highcharts.numberFormat(
                point.series.dataMin,
                Math.abs(point.series.dataMin) < 10000.0 ? 2 : 1,
                ",",
            );
            var max = Highcharts.numberFormat(
                point.series.dataMax,
                Math.abs(point.series.dataMax) < 10000.0 ? 2 : 1,
                ",",
            );

            xyArr.push(
                "<tr>" +
                    '<td><span style="color:' +
                    point.series.color +
                    '">&#x25CF;&nbsp;' +
                    point.series.name +
                    "</span></td>" +
                    '<td><span style="margin-left:' +
                    1 +
                    'em">' +
                    v +
                    "</span></td>" +
                    '<td><span style="margin-left:' +
                    1 +
                    'em">' +
                    min +
                    "</span></td>" +
                    '<td><span style="margin-left:' +
                    1 +
                    'em">' +
                    max +
                    "</span></td>" +
                    "</tr>",
            );
        });

        xyArr.push("</table>");
        return xyArr.join("");
    };

    Highcharts.setOptions({
        time: {
            useUTC: false,
        },
        lang: {
            decimalPoint: ",",
            thousandsSep: ".",
            loading: "Daten werden geladen...",
            months: [
                "Januar",
                "Februar",
                "M\u00e4rz",
                "April",
                "Mai",
                "Juni",
                "Juli",
                "August",
                "September",
                "Oktober",
                "November",
                "Dezember",
            ],
            weekdays: [
                "Sonntag",
                "Montag",
                "Dienstag",
                "Mittwoch",
                "Donnerstag",
                "Freitag",
                "Samstag",
            ],
            shortMonths: [
                "Jan",
                "Feb",
                "M\u00e4r",
                "Apr",
                "Mai",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Okt",
                "Nov",
                "Dez",
            ],
            exportButtonTitle: "Exportieren",
            printButtonTitle: "Drucken",
            rangeSelectorFrom: "vom",
            rangeSelectorTo: "bis",
            rangeSelectorZoom: "", //"Zeitraum",
            downloadPNG: "Download als PNG-Bild",
            downloadJPEG: "Download als JPEG-Bild",
            downloadPDF: "Download als PDF-Dokument",
            downloadSVG: "Download als SVG-Bild",
            resetZoom: "Zoom zur\u00fccksetzen",
            resetZoomTitle: "Zoom zur\u00fccksetzen",
        },
        colors: colors,
        credits: {
            enabled: false,
        },
        plotOptions: {
            series: {
                connectNulls: true,
                allowPointSelect: false,
                cursor: "pointer",
                dataGrouping: {
                    enabled: false,
                    approximation: "average",
                },
            },
        },
        exporting: {
            buttons: {
                contextButton: {
                    enabled: false,
                },
            },
        },
    });
    var chartOptions = {
        rangeSelector: {
            enabled: false,
        },
        chart: {
            type: "line",
            style: { fontFamily: "Arial, Verdana, sans-serif" },
            backgroundColor: null,
            marginBottom: 20,
        },
        title: {
            text: "Einzelwerte",
            style: {
                fontSize: "16px",
                fontWeight: "bold",
            },
        },
        tooltip: {
            formatter: formatter,
            borderWidth: 0,
            backgroundColor: "rgba(219,219,216,0.8)",
            shadow: false,
            useHTML: true,
            shared: true,
            style: {
                fontFamily: "Arial, Helvetica, Clean, sans-serif",
            },
        },
        xAxis: {
            ordinal: false,
            gridLineWidth: 1,
        },
        navigator: {
            margin: 30,
        },
        series: [],
        yAxis: [],
        subtitle: {
            text: "gemessen",
        },
    };
</script>

<div class="display_box" bind:this={chartContainer}></div>
